import { useState } from 'react'

import { createPortal } from 'react-dom'

import { LEGEND_WIDTH } from './constants'
import { defaultPalette, getColorLegendEntries } from './palette'
import type { GeofenceLegendEntry } from './transform/geofence'
import type { LegendGroup, LegendItem } from './types'
import { ALERT_PINS_SERIES, ALERT_SEVERITY_KEY, DOOR_OPEN_SERIES } from './useChartOptions'

export interface OverlayLegendInfo {
  hasDoorOpen: boolean
  hasPower: boolean
  hasMovement: boolean
  doorColorInside: string
  doorColorOutside: string
  powerColor: string
  movementColor: string
  geofences: GeofenceLegendEntry[]
  /** False when geofences are hidden: one plain Door Open row instead of outside/inside. */
  doorsByGeofence: boolean
  hasAlertPins: boolean
}

/** Full color key for the per-zone Mode bars (7 modes), shown in the hover popover. */
const ZONE_MODE_KEY = getColorLegendEntries(defaultPalette, 'zoneMode')

interface TemperatureLegendProps {
  groups: LegendGroup[]
  selected: Record<string, boolean>
  onToggle: (name: string) => void
  onToggleGroup: (groupName: string) => void
  overlays?: OverlayLegendInfo
  /** The "Status bars: On graph / Below" switch; omitted when there are no status bars to move. */
  statusBars?: { below: boolean; onChange: (below: boolean) => void }
}

export default function TemperatureLegend({
  groups,
  selected,
  onToggle,
  onToggleGroup,
  overlays,
  statusBars,
}: TemperatureLegendProps) {
  const renderItem = (item: LegendItem) =>
    item.kind === 'zoneMode' ? (
      <ZoneModeLegendRow
        key={item.name}
        name={item.name}
        label={item.label}
        checked={selected[item.name] ?? true}
        onToggle={onToggle}
      />
    ) : (
      <LegendRow
        key={item.name}
        name={item.name}
        label={item.label}
        color={item.color}
        lineStyle={item.lineStyle}
        dashArray={item.dashArray}
        checked={selected[item.name] ?? true}
        onToggle={onToggle}
      />
    )

  return (
    <div className="overflow-y-auto text-xs shrink-0 pl-2" style={{ width: LEGEND_WIDTH }}>
      {groups.map((group) => {
        const isSingleton = group.items.length === 1 && group.items[0].name === group.groupName
        if (isSingleton) {
          return renderItem(group.items[0])
        }

        // Multi-item group (zone)
        const allOn = group.items.every((i) => selected[i.name] ?? true)
        const allOff = group.items.every((i) => !(selected[i.name] ?? true))
        const indeterminate = !allOn && !allOff

        return (
          <div key={group.groupName} className="mb-0.5">
            <GroupHeader
              groupName={group.groupName}
              checked={allOn}
              indeterminate={indeterminate}
              lineStyle={group.groupLineStyle}
              dashArray={group.groupDashArray}
              onToggle={onToggleGroup}
            />
            <div className="ml-4">{group.items.map((item) => renderItem(item))}</div>
          </div>
        )
      })}
      {overlays && (overlays.hasDoorOpen || overlays.geofences.length > 0 || overlays.hasAlertPins) && (
        <div className="mt-1 border-t border-gray-200 pt-1 text-[11px]">
          {overlays.hasAlertPins && (
            <AlertPinsLegendRow checked={selected[ALERT_PINS_SERIES] ?? true} onToggle={onToggle} />
          )}
          {overlays.geofences.length > 0 &&
            (() => {
              const on = selected['__geofence__'] ?? true
              return (
                <label
                  className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
                  onClick={() => onToggle('__geofence__')}
                >
                  {overlays.geofences.map((gf) => (
                    <SolidSquare key={gf.name} color={on ? gf.color : '#ccc'} />
                  ))}
                  <span className={`truncate ${on ? 'text-gray-600' : 'text-gray-300'}`}>In Geofence</span>
                </label>
              )
            })()}
          {overlays.hasDoorOpen &&
            !overlays.doorsByGeofence &&
            (() => {
              const on = selected[DOOR_OPEN_SERIES] ?? true
              return (
                <label
                  className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
                  onClick={() => onToggle(DOOR_OPEN_SERIES)}
                >
                  <HatchSquare color={on ? overlays.doorColorInside : '#ccc'} />
                  <span className={`truncate ${on ? 'text-gray-600' : 'text-gray-300'}`}>Door Open</span>
                </label>
              )
            })()}
          {overlays.hasDoorOpen &&
            overlays.doorsByGeofence &&
            (() => {
              const outsideOn = selected['__door_open_outside__'] ?? true
              const insideOn = selected['__door_open_inside__'] ?? true
              return (
                <>
                  <label
                    className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
                    onClick={() => onToggle('__door_open_outside__')}
                  >
                    <HatchSquare color={outsideOn ? overlays.doorColorOutside : '#ccc'} />
                    <span className={`truncate ${outsideOn ? 'text-gray-600' : 'text-gray-300'}`}>
                      Door Open (outside)
                    </span>
                  </label>
                  <label
                    className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
                    onClick={() => onToggle('__door_open_inside__')}
                  >
                    <HatchSquare color={insideOn ? overlays.doorColorInside : '#ccc'} />
                    <span className={`truncate ${insideOn ? 'text-gray-600' : 'text-gray-300'}`}>
                      Door Open (inside)
                    </span>
                  </label>
                </>
              )
            })()}
        </div>
      )}
      {statusBars && <StatusBarsToggle {...statusBars} />}
    </div>
  )
}

/** Where the status bars (power, movement, zone mode, alarms) are drawn: on the graph, or
 *  in rows under it with the reefer detail and geofence rows. */
function StatusBarsToggle({ below, onChange }: { below: boolean; onChange: (below: boolean) => void }) {
  const option = (value: boolean, label: string) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={below === value}
      className={`flex-1 px-1.5 py-0.5 ${below === value ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
    >
      {label}
    </button>
  )
  return (
    <div className="mt-1 border-t border-gray-200 px-1 pt-1.5 text-[11px]">
      <div className="mb-1 text-gray-700">Status bars</div>
      <div className="flex overflow-hidden rounded border border-gray-300">
        {option(false, 'On graph')}
        {option(true, 'Below')}
      </div>
    </div>
  )
}

/** Mirrors the severity triangle drawn at the top of each alert pin line. Used for
 *  the single-severity rows in the hover popover. */
function TriangleSwatch({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" className="shrink-0">
      <path d="M6 1 L11.5 11 L0.5 11 Z" fill={color} />
    </svg>
  )
}

/** One triangle carrying all three severity colours, sliced into vertical thirds in
 *  ALERT_SEVERITY_KEY order (least → most severe). Like ZoneModeSwatch it's generic —
 *  it says "this row is colour-coded" without claiming which severities are on the
 *  chart; the popover carries the actual key. Slice edges meet the triangle's sides
 *  at y=4.33, so the three pieces tile it exactly with no clip path needed. */
function AlertSeveritySwatch({ active }: { active: boolean }) {
  const [low, mid, high] = active ? ALERT_SEVERITY_KEY.map((s) => s.line) : ['#ccc', '#ccc', '#ccc']
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" className="shrink-0">
      <path d="M0.5 11 L4.17 4.33 L4.17 11 Z" fill={low} />
      <path d="M4.17 4.33 L6 1 L7.83 4.33 L7.83 11 L4.17 11 Z" fill={mid} />
      <path d="M7.83 4.33 L11.5 11 L7.83 11 Z" fill={high} />
    </svg>
  )
}

/** Alert pins legend row: one triangle per severity present in the window +
 *  togglable label, with a hover popover keying every severity color (rendered
 *  via a portal so the legend's scroll/overflow doesn't clip it). Same shape as
 *  the Zone Mode row, whose colors are likewise too many to list inline. */
function AlertPinsLegendRow({ checked, onToggle }: { checked: boolean; onToggle: (name: string) => void }) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  return (
    <>
      <label
        className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
        onClick={() => onToggle(ALERT_PINS_SERIES)}
        onMouseEnter={(e) => {
          const r = e.currentTarget.getBoundingClientRect()
          setPos({ x: r.right + 6, y: r.top })
        }}
        onMouseLeave={() => setPos(null)}
      >
        <AlertSeveritySwatch active={checked} />
        <span className={`truncate ${checked ? 'text-gray-600' : 'text-gray-300'}`}>Alerts</span>
      </label>
      {pos &&
        createPortal(
          <div
            className="fixed z-50 rounded border border-gray-200 bg-white p-1.5 text-xs shadow-lg"
            style={{ left: pos.x, top: pos.y }}
          >
            <div className="mb-1 font-medium text-gray-700">Severity</div>
            {ALERT_SEVERITY_KEY.map((s) => (
              <div key={s.code} className="flex items-center gap-1.5 py-0.5">
                <TriangleSwatch color={s.line} />
                <span className="text-gray-600">{s.label}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  )
}

function HatchSquare({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" className="shrink-0">
      <rect x="0" y="0" width="12" height="12" fill="#fff" stroke="#d1d5db" strokeWidth="1" />
      <line x1="0" y1="0" x2="12" y2="12" stroke={color} strokeWidth="1.5" opacity="0.5" />
      <line x1="-4" y1="4" x2="8" y2="16" stroke={color} strokeWidth="1.5" opacity="0.5" />
      <line x1="4" y1="-4" x2="16" y2="8" stroke={color} strokeWidth="1.5" opacity="0.5" />
    </svg>
  )
}

/** Generic multi-color swatch for the zone Mode entry — a 2×2 of representative mode
 *  colors hinting that the bar is color-coded, without listing all 7 inline. Grays
 *  out when the lane is toggled off. */
function ZoneModeSwatch({ active }: { active: boolean }) {
  const quad = active ? ZONE_MODE_KEY.slice(0, 4).map((m) => m.color) : ['#ccc', '#ccc', '#ccc', '#ccc']
  const [a, b, c, d] = quad
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" className="shrink-0">
      <rect x="0" y="0" width="6" height="6" fill={a} />
      <rect x="6" y="0" width="6" height="6" fill={b} />
      <rect x="0" y="6" width="6" height="6" fill={c} />
      <rect x="6" y="6" width="6" height="6" fill={d} />
      <rect x="0" y="0" width="12" height="12" fill="none" stroke="#d1d5db" strokeWidth="1" />
    </svg>
  )
}

/** Zone Mode legend row: a generic swatch + togglable label, with a hover popover
 *  keying every mode color (rendered via a portal so the legend's scroll/overflow
 *  doesn't clip it). */
function ZoneModeLegendRow({
  name,
  label,
  checked,
  onToggle,
}: {
  name: string
  label: string
  checked: boolean
  onToggle: (name: string) => void
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  return (
    <>
      <label
        className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
        onClick={() => onToggle(name)}
        onMouseEnter={(e) => {
          const r = e.currentTarget.getBoundingClientRect()
          setPos({ x: r.right + 6, y: r.top })
        }}
        onMouseLeave={() => setPos(null)}
      >
        <ZoneModeSwatch active={checked} />
        <span className={`truncate ${checked ? 'text-gray-600' : 'text-gray-300'}`}>{label}</span>
      </label>
      {pos &&
        createPortal(
          <div
            className="fixed z-50 rounded border border-gray-200 bg-white p-1.5 text-xs shadow-lg"
            style={{ left: pos.x, top: pos.y }}
          >
            <div className="mb-1 font-medium text-gray-700">Modes</div>
            {ZONE_MODE_KEY.map((m) => (
              <div key={m.key} className="flex items-center gap-1.5 py-0.5">
                <span className="inline-block h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: m.color }} />
                <span className="text-gray-600">{m.label}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  )
}

function SolidSquare({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" className="shrink-0">
      <rect x="0" y="0" width="12" height="12" fill={color} opacity="0.3" stroke={color} strokeWidth="1" />
    </svg>
  )
}

function LineCircleIcon({
  color,
  lineStyle = 'solid',
  dashArray: customDashArray,
  active,
}: {
  color: string
  lineStyle?: 'solid' | 'dashed' | 'dotted'
  dashArray?: string
  active: boolean
}) {
  const c = active ? color : '#ccc'
  const da = customDashArray ?? (lineStyle === 'dashed' ? '4,3' : lineStyle === 'dotted' ? '2,2' : undefined)
  return (
    <svg width="20" height="14" viewBox="0 0 20 14" className="shrink-0">
      <line x1="0" y1="7" x2="20" y2="7" stroke={c} strokeWidth="2" strokeDasharray={da} />
      <circle cx="10" cy="7" r="3.5" fill={c} />
    </svg>
  )
}

function GroupCircleIcon({ checked, indeterminate }: { checked: boolean; indeterminate: boolean }) {
  const color = checked || indeterminate ? '#2563eb' : '#ccc'
  const opacity = indeterminate ? 0.5 : 1
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" className="shrink-0" style={{ opacity }}>
      <circle cx="7" cy="7" r="5" fill={color} />
    </svg>
  )
}

function PatternLine({ lineStyle, dashArray }: { lineStyle?: string; dashArray?: string }) {
  const da = dashArray ?? (lineStyle === 'dashed' ? '4,3' : lineStyle === 'dotted' ? '2,2' : undefined)
  if (!da) return null // solid — no indicator needed
  return (
    <svg width="24" height="8" viewBox="0 0 24 8" className="shrink-0">
      <line x1="0" y1="4" x2="24" y2="4" stroke="#6b7280" strokeWidth="2" strokeDasharray={da} />
    </svg>
  )
}

function GroupHeader({
  groupName,
  checked,
  indeterminate,
  lineStyle,
  dashArray,
  onToggle,
}: {
  groupName: string
  checked: boolean
  indeterminate: boolean
  lineStyle?: string
  dashArray?: string
  onToggle: (name: string) => void
}) {
  const labelColor = checked || indeterminate ? 'text-gray-700' : 'text-gray-300'
  return (
    <label
      className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
      onClick={() => onToggle(groupName)}
    >
      <GroupCircleIcon checked={checked} indeterminate={indeterminate} />
      <span className={`font-medium truncate ${labelColor}`}>{groupName}</span>
      <PatternLine lineStyle={lineStyle} dashArray={dashArray} />
    </label>
  )
}

function LegendRow({
  name,
  label,
  color,
  lineStyle = 'solid',
  dashArray,
  checked,
  onToggle,
}: {
  name: string
  label: string
  color: string
  lineStyle?: 'solid' | 'dashed' | 'dotted'
  dashArray?: string
  checked: boolean
  onToggle: (name: string) => void
}) {
  return (
    <label
      className="flex items-center gap-1.5 cursor-pointer py-0.5 hover:bg-gray-50 rounded px-1"
      onClick={() => onToggle(name)}
    >
      <LineCircleIcon color={color} lineStyle={lineStyle} dashArray={dashArray} active={checked} />
      <span className={`truncate ${checked ? 'text-gray-600' : 'text-gray-300'}`}>{label}</span>
    </label>
  )
}
